import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search as SearchIcon, Pencil, EyeOff, Eye, Trash2, Package, Copy } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { createProductAdmin, getProductsAdmin } from '../services/productService';
import { getCategories } from '../services/categoryService';
import { db, isFirebaseConfigured } from '../config/firebase';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';

const getVisibilityLabel = (p) => {
  if (p.deleted) return 'Deleted';
  if (p.visible === false) return 'Hidden';
  if (p.status === 'draft') return 'Draft';
  if (p.status === 'out_of_stock') return 'Out of Stock';
  if (p.inStock === false) return 'Out of Stock';
  if (Number(p.stockQuantity || 0) <= 0) return 'Out of Stock';
  return 'Active';
};

const getPrimaryCategory = (p) => {
  if (Array.isArray(p.categories) && p.categories.length > 0) return p.categories[0];
  if (typeof p.category === 'string') return p.category;
  return '';
};

const getAdminPriceLabel = (p) => {
  const prices = p?.prices && typeof p.prices === 'object' ? p.prices : null;
  if (prices && Object.prototype.hasOwnProperty.call(prices, 'INR')) {
    const n = Number(prices.INR);
    if (Number.isFinite(n)) return `INR ${n}`;
  }
  if (prices) {
    for (const [code, value] of Object.entries(prices)) {
      const n = Number(value);
      if (Number.isFinite(n)) return `${code} ${n}`;
    }
  }
  const legacy = Number(p?.price);
  if (Number.isFinite(legacy)) return `USD ${legacy}`;
  return 'Not Available';
};

const AdminProductsList = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState(() => {
    try {
      return getCategories({ includeHidden: true });
    } catch {
      return [];
    }
  });

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const refresh = async () => {
    setIsLoading(true);
    try {
      const loaded = await getProductsAdmin();
      setProducts(Array.isArray(loaded) ? loaded : []);
    } catch (e) {
      toast({
        title: 'Failed to load products',
        description: String(e?.message || 'Please try again.'),
      });
    } finally {
      setIsLoading(false);
      setSelectedIds(new Set());
    }
  };

  const duplicateProduct = async (product) => {
    try {
      const cloned = {
        ...product,
        name: `${product?.name || 'Untitled product'} (Copy)`,
        status: 'draft',
        visible: false,
        featured: false,
      };

      if (!isFirebaseConfigured || !db) throw new Error('Firebase not configured');

      const { id: newId } = await createProductAdmin({
        ...cloned,
        deleted: false,
      });

      toast({ title: 'Duplicated', description: 'Draft copy created. Edit it and publish when ready.' });
      navigate(`/admin/products/${newId}/edit`);
    } catch (e) {
      toast({ title: 'Duplicate failed', description: String(e?.message || 'Please try again.') });
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const refreshCats = () => {
      try {
        setCategories(getCategories({ includeHidden: true }));
      } catch {
        setCategories([]);
      }
    };
    refreshCats();
    window.addEventListener('ditris:categories', refreshCats);
    return () => window.removeEventListener('ditris:categories', refreshCats);
  }, []);

  const categoryOptions = useMemo(() => {
    const base = categories.map((c) => ({ slug: c.slug, name: c.name }));
    const extras = new Map();
    products.forEach((p) => {
      (p.categories || []).forEach((slug) => {
        if (!slug) return;
        if (!base.find((c) => c.slug === slug)) extras.set(slug, slug.toUpperCase());
      });
      if (p.category && !base.find((c) => c.slug === p.category)) extras.set(p.category, String(p.category).toUpperCase());
    });
    return [...base, ...Array.from(extras.entries()).map(([slug, name]) => ({ slug, name }))];
  }, [products, categories]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products
      .filter((p) => !p.deleted)
      .filter((p) => {
        if (!term) return true;
        return (p.name || '').toLowerCase().includes(term);
      })
      .filter((p) => {
        if (category === 'all') return true;
        const cats = Array.isArray(p.categories) ? p.categories : [];
        return cats.includes(category) || p.category === category;
      })
      .filter((p) => {
        if (status === 'all') return true;
        const label = getVisibilityLabel(p);
        return label.toLowerCase().replace(/\s+/g, '_') === status;
      });
  }, [products, search, category, status]);

  const allVisibleChecked = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));
  const selectedCount = selectedIds.size;

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleChecked) {
        filtered.forEach((p) => next.delete(p.id));
      } else {
        filtered.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateProductFields = async (product, patch) => {
    if (isFirebaseConfigured && db) {
      await updateDoc(doc(db, 'products', product.id), { ...patch, updatedAt: serverTimestamp() });
      await refresh();
      return;
    }

    toast({
      title: 'Not editable yet',
      description: 'This product is not local and Firebase is not ready. Enable Firebase rules/auth to edit it.',
    });
  };

  const softDeleteProduct = async (product) => {
    if (isFirebaseConfigured && db) {
      await updateDoc(doc(db, 'products', product.id), { deleted: true, deletedAt: serverTimestamp() });
      await refresh();
      return;
    }

    toast({
      title: 'Not deletable yet',
      description: 'This product is not local and Firebase is not ready. Enable Firebase rules/auth to delete it.',
    });
  };

  const bulkUpdateVisibility = async (visible) => {
    const targets = products.filter((p) => selectedIds.has(p.id));
    if (targets.length === 0) return;

    try {
      for (const p of targets) {
        // eslint-disable-next-line no-await-in-loop
        await updateProductFields(p, { visible });
      }
      toast({ title: 'Updated', description: visible ? 'Selected products are now visible.' : 'Selected products are now hidden.' });
    } catch (e) {
      toast({ title: 'Bulk update failed', description: String(e?.message || 'Please try again.') });
    }
  };

  const bulkDelete = async () => {
    const targets = products.filter((p) => selectedIds.has(p.id));
    if (targets.length === 0) return;

    const ok = window.confirm(`Soft delete ${targets.length} product(s)?`);
    if (!ok) return;

    try {
      for (const p of targets) {
        // eslint-disable-next-line no-await-in-loop
        await softDeleteProduct(p);
      }
      toast({ title: 'Deleted', description: 'Selected products deleted.' });
    } catch (e) {
      toast({ title: 'Bulk delete failed', description: String(e?.message || 'Please try again.') });
    }
  };


  const openStockManager = (product) => {
    if (!product?.id) return;
    navigate(`/admin/products/${product.id}/stocks`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-wide">LISTED PRODUCTS</h1>
          <p className="mt-2 text-sm text-gray-600">Central control panel for products across local and Firebase sources.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={refresh}
            className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Refresh
          </button>
          <Link
            to="/admin/products/new"
            className="inline-flex items-center justify-center rounded-lg border border-black px-5 py-3 text-sm font-medium tracking-wide hover:bg-black hover:text-white transition-colors"
          >
            ADD PRODUCT
          </Link>
        </div>
      </div>

      <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-5 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-5">
            <label className="block text-xs text-gray-600">Search</label>
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2">
              <SearchIcon size={16} className="text-gray-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by product name"
                className="w-full text-sm outline-none"
              />
            </div>
          </div>

          <div className="lg:col-span-4">
            <label className="block text-xs text-gray-600">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-black transition-colors"
            >
              <option value="all">All categories</option>
              {categoryOptions.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-3">
            <label className="block text-xs text-gray-600">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-black transition-colors"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="out_of_stock">Out of Stock</option>
              <option value="hidden">Hidden</option>
            </select>
          </div>
        </div>

        {selectedCount > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-sm text-gray-700">
              <span className="font-medium text-black">{selectedCount}</span> selected
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => bulkUpdateVisibility(true)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Bulk Show
              </button>
              <button
                type="button"
                onClick={() => bulkUpdateVisibility(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Bulk Hide
              </button>
              <button
                type="button"
                onClick={bulkDelete}
                className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 transition-colors"
              >
                Bulk Delete
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs tracking-widest text-gray-600">
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={allVisibleChecked} onChange={toggleSelectAllVisible} />
                </th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Visibility</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-600">
                    Loading products...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-600">
                    No products found.
                  </td>
                </tr>
              ) : (
                filtered.map((p) => {
                  const cat = getPrimaryCategory(p);
                  const label = getVisibilityLabel(p);
                  const stockQty = Number(p.stockQuantity || 0);
                  const stockLabel = stockQty > 0 && p.inStock !== false ? `${stockQty} in stock` : 'Out of stock';
                  const image = p.images?.[0] || '';

                  return (
                    <tr key={p.id} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-4 py-4 align-top">
                        <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelected(p.id)} />
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex items-center gap-3 min-w-[260px]">
                          <div className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center">
                            {image ? (
                              <img src={image} alt={p.name} className="w-full h-full object-cover" />
                            ) : (
                              <Package size={18} className="text-gray-400" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-black">{p.name || 'Untitled product'}</div>
                            <div className="mt-1 text-xs text-gray-600">ID: {p.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="text-sm text-gray-800">{cat ? cat.toUpperCase().replace(/-/g, ' ') : '-'}</div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="text-sm text-gray-800">{getAdminPriceLabel(p)}</div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className={`text-sm ${stockQty > 0 && p.inStock !== false ? 'text-gray-800' : 'text-red-700'}`}>{stockLabel}</div>
                        <button
                          type="button"
                          onClick={() => openStockManager(p)}
                          className="mt-1 text-xs text-gray-600 hover:text-black transition-colors"
                        >
                          Add/Update Stocks
                        </button>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border ${
                            label === 'Active'
                              ? 'border-green-200 bg-green-50 text-green-800'
                              : label === 'Hidden'
                                ? 'border-gray-200 bg-gray-50 text-gray-700'
                                : label === 'Out of Stock'
                                  ? 'border-red-200 bg-red-50 text-red-800'
                                  : 'border-amber-200 bg-amber-50 text-amber-800'
                          }`}
                        >
                          {label}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              navigate(`/admin/products/${p.id}/edit`);
                            }}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors"
                          >
                            <Pencil size={14} />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => updateProductFields(p, { visible: !p.visible })}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors"
                          >
                            {p.visible === false ? <Eye size={14} /> : <EyeOff size={14} />}
                            {p.visible === false ? 'Show' : 'Hide'}
                          </button>
                          <button
                            type="button"
                            onClick={() => duplicateProduct(p)}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors"
                          >
                            <Copy size={14} />
                            Duplicate
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const ok = window.confirm(`Soft delete “${p.name}”?`);
                              if (!ok) return;
                              softDeleteProduct(p);
                            }}
                            className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default AdminProductsList;
